// Chat column. The conversation that spans the active draft, plus the
// composer. The AI's prose shows here; its <edit> blocks land in the
// editor (the body is never echoed into chat — see EDIT_PROTOCOL_PROMPT).

import { useEffect, useRef, useState } from "react";
import { useComposeStore } from "../../store/composeStore";
import { useKeyStore } from "../../store/keyStore";

export function ChatPane({ onNeedKey }: { onNeedKey: () => void }) {
  const messages = useComposeStore((s) => s.messages);
  const streaming = useComposeStore((s) => s.streaming);
  const streamingText = useComposeStore((s) => s.streamingText);
  const aiEditing = useComposeStore((s) => s.aiEditing);
  const error = useComposeStore((s) => s.error);
  const send = useComposeStore((s) => s.send);
  const provider = useKeyStore((s) => s.provider);
  const canChat = useKeyStore((s) => s.canChat());

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamingText, aiEditing]);

  const providerLabel = provider === "babyai" ? "Bundle of Babies" : "Claude";

  const submit = () => {
    const text = input.trim();
    if (!text || streaming) return;
    if (!canChat) { onNeedKey(); return; }
    setInput("");
    void send(text);
  };

  return (
    <div className="chat-pane">
      <div className="chat-scroll" ref={scrollRef}>
        {messages.length === 0 && !streaming && (
          <div className="chat-empty">
            Ask for a draft, a title, an edit — “write me a short intro post about X,” “tighten the
            second paragraph,” “suggest three titles.” I’ll work in the draft to your right.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`chat-msg chat-${m.role}`}>
            <div className="chat-role">{m.role === "user" ? "you" : "claude"}</div>
            <div className="chat-text">{m.content}</div>
          </div>
        ))}
        {streaming && (
          <div className="chat-msg chat-assistant">
            <div className="chat-role">claude</div>
            <div className="chat-text">
              {streamingText}
              {aiEditing && <span className="chat-editing">✍️ editing your draft…</span>}
              <span className="chat-caret" />
            </div>
          </div>
        )}
      </div>

      {error && <div className="chat-error">{error}</div>}

      <div className="chat-composer">
        <textarea
          className="chat-input"
          rows={2}
          placeholder={`Ask ${providerLabel} to help with your draft…`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
          }}
        />
        <button className="chat-send" disabled={streaming || !input.trim()} onClick={submit}>
          {streaming ? "…" : "Send"}
        </button>
      </div>
      <button className="chat-provider" onClick={onNeedKey} title="change writing model">
        via {providerLabel} · change
      </button>

      <style>{`
        .chat-pane { display: flex; flex-direction: column; height: 100%; min-height: 0; background: var(--paper-raised); }
        .chat-scroll { flex: 1; overflow: auto; padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-4); }
        .chat-empty { color: var(--ink-faint); font-size: 13px; line-height: 1.6; margin-top: var(--space-4); }
        .chat-msg { display: flex; flex-direction: column; gap: 4px; }
        .chat-role {
          font-family: var(--font-mono); font-size: 10px; text-transform: uppercase;
          letter-spacing: 0.08em; color: var(--ink-faint);
        }
        .chat-user .chat-role { color: var(--teal); }
        .chat-text { font-size: 14px; line-height: 1.6; color: var(--ink); white-space: pre-wrap; }
        .chat-user .chat-text {
          background: var(--teal-dim); border-radius: var(--radius-1);
          padding: 8px 12px; align-self: flex-start;
        }
        .chat-editing { display: inline-block; margin-left: 6px; color: var(--teal); font-size: 12px; font-style: italic; }
        .chat-caret { display: inline-block; width: 7px; height: 14px; margin-left: 2px; background: var(--teal); vertical-align: text-bottom; animation: blink 1s steps(2) infinite; }
        @keyframes blink { 0%,50%{opacity:1} 50.01%,100%{opacity:0} }
        .chat-error {
          margin: 0 var(--space-4); padding: var(--space-2) var(--space-3);
          background: var(--rust-bg); border: 1px solid rgba(180,80,42,0.3);
          border-radius: var(--radius-1); color: var(--rust); font-size: 12px; user-select: text;
        }
        .chat-composer { flex: none; display: flex; gap: var(--space-2); padding: var(--space-3); border-top: 1px solid var(--line); }
        .chat-input {
          flex: 1; resize: none; box-sizing: border-box;
          padding: 8px 10px; font-family: var(--font-sans); font-size: 14px;
          background: var(--paper); border: 1px solid var(--line-strong); border-radius: var(--radius-1);
          color: var(--ink);
        }
        .chat-input:focus { outline: none; border-color: var(--teal); }
        .chat-send {
          flex: none; align-self: stretch; padding: 0 16px;
          font-family: var(--font-sans); font-size: 13px; font-weight: 600;
          background: var(--teal); color: #fff; border: none; border-radius: var(--radius-1); cursor: pointer;
        }
        .chat-send:hover { background: var(--teal-bright); }
        .chat-send:disabled { opacity: 0.5; cursor: not-allowed; }
        .chat-provider { align-self: center; margin: 0 0 var(--space-2); background: none; border: none; font-family: var(--font-mono); font-size: 10px; color: var(--ink-faint); cursor: pointer; }
        .chat-provider:hover { color: var(--teal); }
      `}</style>
    </div>
  );
}
